<!-- ══════════════════════════════════════════════════════════════════════════
     TOP BAR — ion-header + ion-toolbar wrapping a transparent 64px strip
     ══════════════════════════════════════════════════════════════════════════
     All affordances compose chrome from theme/components/_pills.scss + _logo.scss.
     The rules in top-bar.component.scss are layout-only (column gaps, container
     padding) and a small set of page-specific overrides.

       Briefing route-pill ........ .pwi-pill --button + local .route-pill
       Back btn (flight detail) ... .pwi-pill --button --compact + .back-btn
       Fleet selector (landscape) . .pwi-pill --sheen + .pwi-segmented* (N seg)
       Fleet trigger (portrait) ... .pwi-pill --button --compact + .fleet-trigger
       Logo ....................... .pwi-logo + .pwi-logo-icon / .pwi-logo-text
       Report selector / filter ... .pwi-pill --button --compact + .report-pill
       Flight info chips .......... .pwi-pill --compact + .center-details-info
       Favourite toggle ........... .pwi-pill --button --circle + .fav-btn
       User chip .................. .pwi-pill --button --sheen + .user-chip
     ══════════════════════════════════════════════════════════════════════════ -->

<ion-header
  class="ion-no-border"
  [class.scrolled]="rs.pageScrolled()"
  [class.flight-detail]="fs.detailOverlayShowing()"
  [class.briefing-loaded]="rs.currentView() === appViews.briefing && bs.showBriefing()">
  <ion-toolbar>
    <div class="top-bar-grid">
      <!-- ─── Col 1: Left — fleet navigation, back button, or briefing route pill ─ -->
      <div class="top-bar-left flex-row items-center">
        @if (rs.currentView() === appViews.briefing && bs.showBriefing()) {
          <!-- Briefing route-pill — replaces the fleet nav on the loaded
               briefing view. Clicking returns to the input form via
               bs.clearBriefingFlight(). -->
          <button
            class="pwi-pill pwi-pill--button route-pill"
            type="button"
            (click)="bs.clearBriefingFlight()"
            title="Edit route">
            <fa-icon
              class="route-pill-back"
              [icon]="['fas', 'chevron-left']"></fa-icon>
            @if (bs.briefingRequest().fromIcao.length === 4) {
              <span class="route-leg route-leg--dep">
                <fa-icon
                  class="route-leg-icon"
                  [icon]="['fas', 'plane-departure']"></fa-icon>
                <span class="route-icao">{{ bs.briefingRequest().fromIcao }}</span>
                <span class="route-rwy">{{ bs.briefingRequest().fromRunway || 'ALL' }}</span>
              </span>
            }
            @if (bs.briefingRequest().fromIcao.length === 4 && bs.briefingRequest().toIcao.length === 4) {
              <fa-icon
                class="route-sep"
                [icon]="['fas', 'arrow-right']"></fa-icon>
            }
            @if (bs.briefingRequest().toIcao.length === 4) {
              <span class="route-leg route-leg--arr">
                <fa-icon
                  class="route-leg-icon"
                  [icon]="['fas', 'plane-arrival']"></fa-icon>
                <span class="route-icao">{{ bs.briefingRequest().toIcao }}</span>
                <span class="route-rwy">{{ bs.briefingRequest().toRunway || 'ALL' }}</span>
              </span>
            }
            <span class="route-pill-divider"></span>
            <span class="route-aircraft-group">
              <fa-icon
                class="route-plane"
                [icon]="['fas', 'plane']"></fa-icon>
              <span class="route-aircraft">{{ ds.selectedFleet()?.name || '' }}</span>
            </span>
          </button>
        } @else if (rs.currentView() !== appViews.briefing) {
          @if (fs.detailOverlayShowing()) {
            <!-- Back button (flight detail open full screen — a flight embedded
                 by the flights split view keeps the list chrome). Compact glass
                 pill with a leading chevron + the previous view's label. -->
            <button
              type="button"
              class="pwi-pill pwi-pill--button pwi-pill--compact back-btn"
              (click)="fs.stopEditing()">
              <fa-icon [icon]="['fas', 'chevron-left']"></fa-icon>
              <span class="back-btn-text">{{ rs.currentViewLabel() }}</span>
            </button>
          } @else if (ds.availableFleets().length > 0) {
            <!-- Rendered from one fleet up: a lone fleet reads as a current-fleet
                 indicator, several fleets switch between them. -->
            @if (!rs.isPortrait()) {
              <!-- Landscape: pwi-segmented selector (SARAPWI.Front .aircraft-selector parity) -->
              <div
                class="pwi-pill pwi-pill--sheen pwi-segmented"
                tourAnchor="topbar.fleet-selector">
                @for (fleet of ds.availableFleets(); track $index) {
                  <button
                    type="button"
                    class="pwi-segmented-btn"
                    [class.pwi-segmented-btn--active]="(fleet.id || 0) === ds.selectedFleetId()"
                    (click)="ds.setCurrentFleet(fleet.id)">
                    <span class="text-ellipsis">{{ fleet.name }}</span>
                  </button>
                }
              </div>
            } @else {
              <!-- Portrait: compact glass pill. Several fleets → a menu trigger
                   (chevron + mat-menu). A single fleet → a static indicator that
                   can't open, matching landscape where a lone fleet is just the
                   current-fleet segment with nothing to switch to. -->
              @if (ds.availableFleets().length > 1) {
                <button
                  type="button"
                  class="pwi-pill pwi-pill--button pwi-pill--compact fleet-trigger"
                  [matMenuTriggerFor]="fleetMenu">
                  <span class="text-ellipsis">{{ ds.selectedFleet()?.name || 'Select Fleet' }}</span>
                  <fa-icon [icon]="['fas', 'chevron-down']"></fa-icon>
                </button>
                <mat-menu #fleetMenu="matMenu">
                  @for (fleet of ds.availableFleets(); track $index) {
                    <button
                      type="button"
                      mat-menu-item
                      [disabled]="(fleet.id || 0) === ds.selectedFleetId()"
                      (click)="ds.setCurrentFleet(fleet.id)">
                      {{ fleet.name }}
                      @if ((fleet.id || 0) === ds.selectedFleetId()) {
                        <ion-icon name="checkmark"></ion-icon>
                      }
                    </button>
                  }
                </mat-menu>
              } @else {
                <!-- Single fleet — static, non-opening indicator (no chevron, no menu). -->
                <div class="pwi-pill pwi-pill--compact fleet-trigger">
                  <span class="text-ellipsis">{{ ds.selectedFleet()?.name }}</span>
                </div>
              }
            }
          }
        }
      </div>

      <!-- ─── Col 2: Center — logo (home) / flights controls / reports controls ── -->
      <div class="top-bar-center flex-row items-center justify-center">
        @if (fs.detailOverlayShowing()) {
          <div class="flights-center-controls flex-row items-center">
            <ng-container *ngTemplateOutlet="FlightDetailsTopBar"></ng-container>
          </div>
        } @else if (rs.currentView() === appViews.reports && reportsStore.reportsAccessAllowed()) {
          <!-- REGION: Reports controls — report selector + filters. Gated on
               reportsAccessAllowed: when restricted, the centre falls back to the
               logo (and the right column to the user chip), matching the home chrome. -->
          <div class="reports-center-controls flex-row items-center">
            <!-- Report selector — the title itself is the dropdown trigger
                 (bold page title + chevron), matching the model. -->
            <button
              type="button"
              class="report-title-btn"
              tourAnchor="topbar.report-picker"
              [matMenuTriggerFor]="reportMenu">
              <span class="page-title">{{ reportsStore.selectedReportLabel() }}</span>
              <fa-icon
                class="report-title-chev"
                [icon]="['fas', 'chevron-down']"></fa-icon>
            </button>
            <!-- Styled to match the filters dropdown (.filter-options) via the
                 report-select-menu panel class — see top-bar.component.scss. -->
            <mat-menu
              #reportMenu="matMenu"
              class="report-select-menu">
              <!-- Reports grouped under the backend's category headers. The
                   header row is a non-interactive label, not a menu item. The
                   label-less uncategorized bucket gets NO header (design) — but
                   when it trails labelled categories it gets a hairline
                   separator so its reports don't read as part of the last
                   category. A label-less FIRST group is the no-categories
                   fallback → bare flat list, nothing rendered above it. -->
              @for (group of reportsStore.reportGroups(); track group.id) {
                @if (group.label) {
                  <div class="report-group-label">{{ group.label }}</div>
                } @else if (!$first) {
                  <div class="report-group-separator"></div>
                }
                @for (report of group.reports; track report.slug) {
                  <button
                    type="button"
                    mat-menu-item
                    [class.report-menu-item-selected]="reportsStore.selectedReport().slug === report.slug"
                    [disabled]="reportsStore.selectedReport().slug === report.slug"
                    (click)="reportsStore.selectReport(report.slug)">
                    <span>{{ report.label }}</span>
                    @if (reportsStore.selectedReport().slug === report.slug) {
                      <fa-icon [icon]="['fas', 'check']"></fa-icon>
                    }
                  </button>
                }
              }
            </mat-menu>

            <!-- Filters — reuse the shared <report-filters> popover (same as the
                 briefing view); all filter state lives in ReportsStore. -->
            @if (reportsStore.currentReportFilters().length > 0) {
              <report-filters
                tourAnchor="topbar.report-filters"
                [filters]="reportsStore.displayFilters()"
                [filtersOpen]="reportsStore.filtersOpen()"
                [pendingFilters]="reportsStore.pendingFilters()"
                [filterDropdownOpen]="reportsStore.filterDropdownOpen()"
                [activeFilterCount]="reportsStore.activeFilterCount()"
                [applicabilityOptions]="reportsStore.applicabilityOptions()"
                (openPopover)="reportsStore.openFilters()"
                (closePopover)="reportsStore.closeFilters()"
                (toggleDropdown)="reportsStore.toggleFilterDropdown($event)"
                (updateScalar)="reportsStore.updatePendingScalar($event.key, $event.value)"
                (applyFilters)="reportsStore.applyFilters()"
                (resetFilters)="reportsStore.resetFilters()"></report-filters>
            }
          </div>
        } @else if (rs.currentView() === appViews.flights) {
          <!-- Flights controls — scope tabs + Filters in the header centre,
               wired to FlightsStore (same pattern as the reports controls). -->
          <div class="flights-list-controls flex-row items-center">
            <!-- Scope segment — All / To review / Bookmarks -->
            <div
              class="pwi-pill pwi-segmented pwi-segmented--accent pwi-pill--sheen scope-tabs"
              tourAnchor="topbar.flight-scope"
              role="tablist">
              <button
                type="button"
                class="pwi-segmented-btn"
                role="tab"
                [class.pwi-segmented-btn--active]="fs.flightScope() === 'all'"
                [attr.aria-selected]="fs.flightScope() === 'all'"
                (click)="fs.setFlightScope('all')">
                All
              </button>
              <button
                type="button"
                class="pwi-segmented-btn"
                role="tab"
                [class.pwi-segmented-btn--active]="fs.flightScope() === 'toReview'"
                [attr.aria-selected]="fs.flightScope() === 'toReview'"
                (click)="fs.setFlightScope('toReview')">
                To review
              </button>
              <button
                type="button"
                class="pwi-segmented-btn"
                role="tab"
                [class.pwi-segmented-btn--active]="fs.flightScope() === 'favorites'"
                [attr.aria-selected]="fs.flightScope() === 'favorites'"
                (click)="fs.setFlightScope('favorites')">
                <fa-icon
                  class="scope-fav-icon"
                  [icon]="['fas', 'bookmark']"></fa-icon>
                Bookmarks
              </button>
            </div>

            <!-- Filter button + dropdown anchor -->
            <div
              class="filter-wrapper"
              tourAnchor="topbar.flight-filters">
              <button
                type="button"
                class="toolbar-btn"
                [class.toolbar-btn--active]="fs.filtersOpen() || fs.hasActiveFilters()"
                (click)="fs.toggleFiltersOpen(); closeLegend()">
                <fa-icon
                  class="filter-icon"
                  [icon]="['fas', 'filter']"></fa-icon>
                <span>Filters</span>
                @if (fs.activeFilterCount() > 0) {
                  <span class="toolbar-btn-badge">{{ fs.activeFilterCount() }}</span>
                } @else {
                  <fa-icon
                    class="filter-chevron"
                    [icon]="['fas', 'chevron-down']"
                    [class.rotated]="fs.filtersOpen()"></fa-icon>
                }
              </button>

              @if (fs.filtersOpen()) {
                <div
                  class="filter-backdrop"
                  tabindex="0"
                  role="button"
                  (click)="fs.closeFilters()"
                  (keydown.enter)="fs.closeFilters()"></div>
                <div class="filter-dropdown">
                  <div class="filter-header">Filters</div>

                  <!-- Boolean toggles — drive setFilter on FlightsStore. -->
                  <div class="filter-list">
                    <!-- Month — the backend's selectable months (filter-values),
                         staged as a fromDate/toDate range on the draft criteria
                         and committed by Apply like every other filter. First
                         row, matching the reference layout. -->
                    <div class="filter-row">
                      <span class="filter-row-label">Month</span>
                      <div class="period-wrapper">
                        <button
                          type="button"
                          class="period-btn pwi-pill pwi-pill--button"
                          (click)="fs.toggleMonthDropdown()">
                          {{ monthFilterLabel() }}
                          <fa-icon
                            class="period-chevron"
                            [class.rotated]="fs.monthDropdownOpen()"
                            [icon]="['fas', 'chevron-down']"></fa-icon>
                        </button>
                        @if (fs.monthDropdownOpen()) {
                          <div
                            class="period-backdrop"
                            tabindex="0"
                            role="button"
                            (click)="fs.closeMonthDropdown(); $event.stopPropagation()"
                            (keydown.enter)="fs.closeMonthDropdown(); $event.stopPropagation()"></div>
                          <ul class="period-dropdown">
                            <li
                              class="period-option"
                              tabindex="0"
                              role="button"
                              [class.period-option--active]="!fs.currentFilters().month"
                              (click)="fs.setMonthFilter(''); $event.stopPropagation()"
                              (keydown.enter)="fs.setMonthFilter(''); $event.stopPropagation()">
                              Any month
                              @if (!fs.currentFilters().month) {
                                <fa-icon [icon]="['fas', 'check']"></fa-icon>
                              }
                            </li>
                            @for (m of fs.monthOptions(); track m.value) {
                              <li
                                class="period-option"
                                tabindex="0"
                                role="button"
                                [class.period-option--active]="fs.currentFilters().month === m.value"
                                (click)="fs.setMonthFilter(m.value); $event.stopPropagation()"
                                (keydown.enter)="fs.setMonthFilter(m.value); $event.stopPropagation()">
                                {{ m.label }}
                                @if (fs.currentFilters().month === m.value) {
                                  <fa-icon [icon]="['fas', 'check']"></fa-icon>
                                }
                              </li>
                            }
                          </ul>
                        }
                      </div>
                    </div>
                    <div class="filter-row">
                      <span class="filter-row-label">PF only (TO or LND)</span>
                      <ion-toggle
                        [checked]="fs.currentFilters().pilotFlying"
                        (ionChange)="fs.setFilter($any($event).detail.checked, filterKey.pilotFlying)" />
                    </div>
                    <div class="filter-row">
                      <span class="filter-row-label">Events only</span>
                      <ion-toggle
                        [checked]="fs.currentFilters().hasEvents"
                        (ionChange)="fs.setFilter($any($event).detail.checked, filterKey.hasEvents)" />
                    </div>
                    <div class="filter-row">
                      <span class="filter-row-label">Unread comments only</span>
                      <ion-toggle
                        [checked]="fs.currentFilters().hasUnreadComments"
                        (ionChange)="fs.setFilter($any($event).detail.checked, filterKey.hasUnreadComments)" />
                    </div>
                    <div class="filter-row">
                      <span class="filter-row-label">With data only</span>
                      <ion-toggle
                        [checked]="fs.currentFilters().dataAvailable"
                        (ionChange)="fs.setFilter($any($event).detail.checked, filterKey.dataAvailable)" />
                    </div>
                  </div>

                  <!-- List pickers (origin / destination / registration). -->
                  <div class="filter-list">
                    @for (lf of listFilters; track lf.key) {
                      <div
                        class="filter-row"
                        [class.filter-row--last]="$last">
                        <span class="filter-row-label">{{ lf.label }}</span>
                        <div
                          class="filter-select"
                          tabindex="0"
                          role="button"
                          (click)="fs.toggleListFilterDropdown(lf.key)"
                          (keydown.enter)="fs.toggleListFilterDropdown(lf.key)">
                          <input
                            autocorrect="off"
                            class="filter-select-input"
                            type="text"
                            [placeholder]="lf.placeholder"
                            [attr.maxlength]="lf.maxLength"
                            [ngModel]="fs.listFilterValues()[lf.key]"
                            (ngModelChange)="fs.setListFilterSingle(lf.key, $event)"
                            (click)="fs.openListFilterDropdown(lf.key); $event.stopPropagation()" />
                          <svg
                            class="filter-select-icon"
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                            tabindex="0"
                            role="button"
                            (click)="$event.stopPropagation(); fs.toggleListFilterDropdown(lf.key)"
                            (keydown.enter)="$event.stopPropagation(); fs.toggleListFilterDropdown(lf.key)">
                            <polyline
                              points="2,4 5,7 8,4"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              fill="none" />
                          </svg>
                          @if (fs.openListFilter() === lf.key) {
                            <div
                              class="filter-select-backdrop"
                              tabindex="0"
                              role="button"
                              (click)="fs.closeListFilterDropdown(); $event.stopPropagation()"
                              (keydown.enter)="fs.closeListFilterDropdown(); $event.stopPropagation()"></div>
                            <ul
                              class="filter-select-panel"
                              tabindex="0"
                              role="button"
                              (click)="$event.stopPropagation()"
                              (keydown.enter)="$event.stopPropagation()">
                              @if (fs.displayedListCompletions()[lf.key].length === 0) {
                                <li class="filter-select-option filter-select-option--empty">No matches</li>
                              } @else {
                                @for (option of fs.displayedListCompletions()[lf.key]; track option) {
                                  <li
                                    class="filter-select-option"
                                    tabindex="0"
                                    role="button"
                                    [class.filter-select-option--active]="fs.listFilterValues()[lf.key] === option"
                                    (click)="fs.selectListFilterValue(lf.key, option); $event.stopPropagation()"
                                    (keydown.enter)="
                                      fs.selectListFilterValue(lf.key, option); $event.stopPropagation()
                                    ">
                                    {{ option }}
                                  </li>
                                }
                              }
                            </ul>
                          }
                        </div>
                      </div>
                    }
                  </div>

                  <div class="filter-footer">
                    <button
                      type="button"
                      class="filter-reset-btn"
                      (click)="fs.clearAllFilters()">
                      Reset
                    </button>
                    <button
                      type="button"
                      class="filter-apply-btn"
                      [disabled]="!fs.hasPendingChanges()"
                      (click)="fs.applyFilters()">
                      Apply
                    </button>
                  </div>
                </div>
              }
            </div>

            <!-- Legend — compact card colour-code reference popover. -->
            <div class="legend-wrapper">
              <button
                mat-icon-button
                class="pwi-info-btn"
                [class.pwi-info-btn--active]="legendOpen()"
                (click)="toggleLegend()"
                matTooltip="Card colour legend"
                aria-label="Card colour legend">
                <sara-icon
                  icon="circle-info"
                  variant="solid"></sara-icon>
              </button>

              @if (legendOpen()) {
                <div
                  class="legend-backdrop"
                  tabindex="0"
                  role="button"
                  (click)="closeLegend()"
                  (keydown.enter)="closeLegend()"></div>
                <div class="legend-popover">
                  <!-- Hairline-separated header, same pattern as the app's modals. -->
                  <div class="legend-title">Legend</div>
                  <div class="legend-body">
                    <div class="legend-section">
                      <div class="legend-section-label">Status</div>
                      <div class="legend-row">
                        <span class="legend-swatch legend-swatch--to-review"></span>
                        <span class="legend-label">To review</span>
                      </div>
                      <div class="legend-row">
                        <span class="legend-swatch legend-swatch--reviewed"></span>
                        <span class="legend-label">Reviewed</span>
                      </div>
                      <div class="legend-row">
                        <!-- fxyllw client scheme: the no-data swatch follows the
                             cards' yellow re-tint on fdx sessions (dormant while
                             YELLOW_SCHEME_ENABLED is off — see data.store.ts). -->
                        <span
                          class="legend-swatch legend-swatch--no-data"
                          [class.legend-swatch--no-data-yellow]="ds.isYellowScheme()"></span>
                        <span class="legend-label">No data</span>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        } @else if (rs.currentView() === appViews.home) {
          <!-- Logo — centre column on HOME only. Everywhere else it lives in the
               right column: flights/reports hold page controls in the centre;
               flight details + the LOADED briefing move it right for the route-pill;
               and the briefing INPUT form now matches flights/reports too (logo
               right, no user chip). Centering comes from the parent
               .top-bar-center's justify-center utility. -->
          <div class="pwi-logo">
            <img
              class="pwi-logo-icon"
              src="assets/logos/sarapwi-image-only-dark-320px-72ppi.png"
              alt="" />
            <img
              class="pwi-logo-text"
              src="assets/logos/sarapwi-text-only-dark-500px-72ppi.png"
              alt="Sara PWI" />
          </div>
        }
      </div>

      <!-- ─── Col 3: Right — user chip (home) / Sara PWI logo (everywhere else) ── -->
      <div class="top-bar-right flex-row items-center justify-end">
        <!-- Dev-only reminder: a UI override from Settings → Developer is
             forcing a state on this screen's data, so what's on screen is not
             what the data says. Never renders on pilot builds (the overrides
             are inert there). -->
        @if (uio.active()) {
          <span
            class="pwi-pill pwi-pill--compact dev-overrides-pill"
            matTooltip="UI overrides are on (Settings → Developer)">
            UI overrides
          </span>
        }
        <!-- Connectivity status chip — EF-1.2's always-visible indicator:
             discreet online, explicit offline; click = state details (status,
             last server contact, pending actions). Ships dark with the
             OfflineMode flag. -->
        @if (ffs.isEnabled('OfflineMode')) {
          <connectivity-status />
        }
        <!-- Guided-tour restart "?" — replays the current page's tour (design:
             "restart any time from the ? button in the top bar"). No longer flag
             gated: the tours are mature and ship on. -->
        @if (!toursStore.helpButtonHidden()) {
          <button
            mat-icon-button
            class="tour-help-btn"
            matTooltip="Show me around"
            aria-label="Restart the page tour"
            (click)="toursStore.restartTour(currentTourId())">
            <fa-icon [icon]="['fas', 'circle-question']"></fa-icon>
          </button>
        }
        <!-- Flights layout toggle — grid / split (FlightsSplitView flag). Only on
             the flights list, and only while the viewport can show the split (a
             landscape, non-narrow screen); the pilot's choice survives a hidden
             toggle. Sits before the logo, like the help button. -->
        @if (
          rs.currentView() === appViews.flights &&
          !fs.detailOverlayShowing() &&
          ffs.isEnabled('FlightsSplitView') &&
          fs.splitViewAvailable()
        ) {
          <div
            class="pwi-pill pwi-segmented pwi-segmented--accent pwi-pill--sheen view-toggle"
            tourAnchor="topbar.flight-layout"
            role="tablist"
            aria-label="Flights layout">
            <button
              type="button"
              class="pwi-segmented-btn view-toggle-btn"
              role="tab"
              [class.pwi-segmented-btn--active]="fs.flightsViewMode() === 'grid'"
              [attr.aria-selected]="fs.flightsViewMode() === 'grid'"
              matTooltip="Grid"
              aria-label="Grid view"
              (click)="fs.setFlightsViewMode('grid')">
              <fa-icon [icon]="['fas', 'grip']"></fa-icon>
            </button>
            <button
              type="button"
              class="pwi-segmented-btn view-toggle-btn"
              role="tab"
              [class.pwi-segmented-btn--active]="fs.flightsViewMode() === 'split'"
              [attr.aria-selected]="fs.flightsViewMode() === 'split'"
              matTooltip="Split view"
              aria-label="Split view"
              (click)="fs.setFlightsViewMode('split')">
              <fa-icon [icon]="['fas', 'table-columns']"></fa-icon>
            </button>
          </div>
        }
        @if (
          fs.detailOverlayShowing() ||
          (rs.currentView() === appViews.reports && reportsStore.reportsAccessAllowed()) ||
          rs.currentView() === appViews.flights ||
          rs.currentView() === appViews.briefing
        ) {
          <!-- Logo anchors the right column on every non-home view: flights (scope
               tabs + filters), flight details, reports, and BOTH briefing states
               (input form + loaded). The user chip is dropped on these (SARAPWI.Front
               parity); settings stay reachable from the home view's user chip. -->
          <div class="pwi-logo">
            <img
              class="pwi-logo-icon"
              src="assets/logos/sarapwi-image-only-dark-320px-72ppi.png"
              alt="" />
            <img
              class="pwi-logo-text"
              src="assets/logos/sarapwi-text-only-dark-500px-72ppi.png"
              alt="Sara PWI" />
          </div>
        } @else {
          <!-- Home only: user chip opens settings (other views drop it, see above). -->
          <button
            type="button"
            class="pwi-pill pwi-pill--button pwi-pill--sheen user-chip"
            tourAnchor="topbar.user-menu"
            (click)="rs.displaySettings(true)"
            matTooltip="open settings">
            <fa-icon [icon]="['fas', 'user']"></fa-icon>
            <span class="user-name text-ellipsis">{{ ds.userName() }}</span>
            <fa-icon [icon]="['fas', 'chevron-down']"></fa-icon>
          </button>
        }
      </div>
    </div>
  </ion-toolbar>
</ion-header>

<ng-template #FlightDetailsTopBar>
  <div
    class="center-details-control flex-row items-center"
    tourAnchor="flight-details.header">
    <!-- Flight number — display-only chip -->
    <div class="pwi-pill pwi-pill--compact center-details-info">
      <span class="flight-number">
        <span class="hash">#</span>
        {{ fs.selectedFlight()?.flightNumberFromMovementLog }}
      </span>
    </div>
    <!-- Aircraft registration -->
    <div class="pwi-pill pwi-pill--compact center-details-info">
      <sara-icon
        class="icon-xs"
        colorScheme="all-blue-soft"
        icon="plane-tail"></sara-icon>
      <span class="aircraft-registration">{{ fs.selectedFlight()?.aircraftRegistration }}</span>
    </div>
    <!-- Aircraft type -->
    <div class="pwi-pill pwi-pill--compact center-details-info">
      <sara-icon
        class="icon-xs"
        colorScheme="all-blue-soft"
        icon="plane"></sara-icon>
      <span class="aircraft-type">{{ fs.selectedFlight()?.type }}</span>
    </div>

    <!-- Bookmark toggle — circular glass button. The bookmark swaps solid (filled)
         when bookmarked ↔ regular (outline) when not, same fas/far pattern as the
         flight-card bookmark. -->
    <button
      type="button"
      class="pwi-pill pwi-pill--button pwi-pill--circle fav-btn"
      [class.fav-btn--favourited]="fs.selectedFlight()?.isFavourite"
      [attr.aria-pressed]="!!fs.selectedFlight()?.isFavourite"
      [attr.aria-label]="fs.selectedFlight()?.isFavourite ? 'Remove from bookmarks' : 'Add to bookmarks'"
      (click)="fs.toggleFavourite(fs.selectedFlight()!)">
      <fa-icon [icon]="[fs.selectedFlight()?.isFavourite ? 'fas' : 'far', 'bookmark']"></fa-icon>
    </button>
  </div>
</ng-template>
